import React, { useState, useCallback } from 'react';
import {
  Paper,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  TextField,
  Grid,
  InputAdornment,
  Button,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import DateRangeIcon from '@mui/icons-material/DateRange';

const ClosedTransactionsGrid = ({ selectedAgency, canViewRequestingAgency }) => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchRef, setSearchRef] = useState('');
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
      .toISOString()
      .slice(0, 10)
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));

  // Fetch the closed transactions data
  const fetchClosedTransactions = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('jwtToken');
    if (!token || !selectedAgency) {
      toast.error('No authentication token or selected agency found.');
      setLoading(false);
      return;
    }

    try {
      // Validate date range (maximum 90 days)
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffInDays = (end - start) / (1000 * 60 * 60 * 24);

      if (diffInDays > 90) {
        toast.error('Date range should not exceed 90 days.');
        setLoading(false);
        return;
      }

      if (start > end) {
        toast.error('Start date cannot be after end date.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        startDate,
        endDate,
      });

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}PostingTransferBond/${selectedAgency.id}/Closed?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error fetching closed transactions: ${errorText}`);
      }

      const data = await response.json();

      // Process the data as before
      const processedData = data.map((transaction) => {
        let createdAtFormatted = 'N/A';
        if (transaction.createdAt) {
          const dateObj = new Date(transaction.createdAt);
          const dateStr = dateObj.toLocaleDateString('en-US');
          const timeStr = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          createdAtFormatted = { date: dateStr, time: timeStr };
        }

        return {
          id: transaction.transferBondTransactionID,
          refNumber: transaction.transferBondTransactionID.toString(),
          createdAt: transaction.createdAt
            ? new Date(transaction.createdAt)
            : null,
          createdAtFormatted: createdAtFormatted,
          totalBondAmount: transaction.totalBondAmount
            ? `$${transaction.totalBondAmount.toLocaleString()}`
            : 'N/A',
          county: transaction.county || 'N/A',
          state: transaction.state || 'N/A',
          isOnline: transaction.isOnline,
          defendantName: `${transaction.defendantFirstName?.trim() || ''} ${transaction.defendantLastName?.trim() || ''}`.trim() || 'N/A',
          requestingAgencyName: transaction.requestingAgencyName || '-',
        };
      });

      setRecords(processedData);
    } catch (error) {
      toast.error(error.message || 'Error fetching closed transactions.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedAgency]);

  const handleViewDetails = (id) => {
    navigate(`/view-quote-details?ID=${id}`);
  };

  const handleSearch = () => {
    fetchClosedTransactions();
  };

  // Client-side filtering based on reference number
  const getFilteredRows = () => {
    let filteredRecords = records;

    if (searchRef.trim() !== '') {
      filteredRecords = filteredRecords.filter((record) =>
        record.refNumber.includes(searchRef.trim())
      );
    }

    return filteredRecords;
  };

  const columns = [
    {
      field: 'refNumber',
      headerName: 'Ref #',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          height: '100%',
          width: '100%'
        }}>
          <Typography variant="body2">{params.value}</Typography>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleViewDetails(params.row.id)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'defendantName',
      headerName: 'Defendant Name',
      width: 250,
      align: 'center',
      headerAlign: 'center',
    },
    ...(canViewRequestingAgency
      ? [
          {
            field: 'requestingAgencyName',
            headerName: 'Requesting Agency',
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                  width: '100%',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                }}
              >
                {params.row.requestingAgencyName || '-'}
              </Box>
            ),
          },
        ]
      : []),
    {
      field: 'createdAtFormatted',
      headerName: 'Created At',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (params.value === 'N/A' || typeof params.value === 'string') {
          return <Typography variant="body2">N/A</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2">{params.value.date}</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
              {params.value.time}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'origination',
      headerName: 'Origin',
      width: 175,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (params.row.isOnline === true) {
          return <span style={{ fontStyle: 'italic' }}>Online</span>;
        }
        return <span style={{ fontStyle: 'italic' }}>Bankers In-House</span>;
      },
    },
    {
      field: 'totalBondAmount',
      headerName: 'Total Bond Amount',
      width: 150,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'county',
      headerName: 'County',
      width: 100,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'state',
      headerName: 'State',
      width: 80,
      align: 'center',
      headerAlign: 'center',
    },
  ];

  return (
    <Paper sx={{ padding: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Closed Transactions
      </Typography>

      {/* Instructions */}
      <Typography variant="body2" sx={{ mb: 2 }}>
        • You can search closed transactions within a 90-day date range.
        <br />
        • Use the "Filter by Reference #" input to filter the displayed results.
      </Typography>

      {/* Date Range Filters */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Date Range Inputs */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {/* Search Button */}
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSearch}
              disabled={loading}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Reference Number Filter */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <TextField
          label="Filter by Reference #"
          value={searchRef}
          onChange={(e) => setSearchRef(e.target.value)}
          fullWidth
          placeholder="Type to filter displayed results..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="textSecondary">
          This filters the displayed data based on the reference number.
        </Typography>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <CircularProgress />
        </Box>
      ) : getFilteredRows().length > 0 ? (
        <Paper sx={{ width: '100%' }}>
          <DataGrid
            rows={getFilteredRows()}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            pageSizeOptions={[10]}
            pagination
            autoHeight
            sx={{ fontSize: '0.85rem' }}
          />
        </Paper>
      ) : (
        // No data
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <Typography variant="body1" color="textSecondary">
            No closed transactions found.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ClosedTransactionsGrid;
